import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useLocations } from '../../hooks/useWmsLocations';
import type { ReceivingLine, ReceiveLineInput, DeviceStatus } from '../../types';

interface SerialEntry {
  serial_number: string;
  mac_address: string;
  dev_id: string;
  device_status: DeviceStatus;
  notes: string;
}

const emptySerial = (): SerialEntry => ({
  serial_number: '',
  mac_address: '',
  dev_id: '',
  device_status: 'valid',
  notes: '',
});

const deviceStatusOptions: { value: DeviceStatus; label: string }[] = [
  { value: 'valid', label: 'Valid' },
  { value: 'verify', label: 'Verify' },
  { value: 'test', label: 'Test' },
  { value: 'dev', label: 'Dev' },
  { value: 'dead', label: 'Dead' },
];

interface ReceiveLineModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReceiveLineInput) => Promise<void>;
  line: ReceivingLine | null;
  isLoading?: boolean;
}

export function ReceiveLineModal({
  open,
  onClose,
  onSubmit,
  line,
  isLoading,
}: ReceiveLineModalProps) {
  const [receivedQuantity, setReceivedQuantity] = useState('');
  const [locationId, setLocationId] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isSerialized, setIsSerialized] = useState(false);
  const [serials, setSerials] = useState<SerialEntry[]>([]);

  // Fetch available locations for dropdown
  const { data: locationsData, isLoading: locationsLoading } = useLocations({
    status: 'active',
    page_length: 100,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setReceivedQuantity('');
      setLocationId('');
      setLotNumber('');
      setExpirationDate('');
      setNotes('');
      setIsSerialized(false);
      setSerials([]);
    }
  }, [open]);

  // Sync serial entries with received quantity
  useEffect(() => {
    if (!isSerialized) {
      setSerials([]);
      return;
    }
    const qty = parseInt(receivedQuantity, 10);
    if (isNaN(qty) || qty <= 0) {
      setSerials([]);
      return;
    }

    setSerials(prev => {
      if (qty > prev.length) {
        // Add more serial entries
        const newSerials = [...prev];
        for (let i = prev.length; i < qty; i++) {
          newSerials.push(emptySerial());
        }
        return newSerials;
      } else if (qty < prev.length) {
        // Remove excess serial entries
        return prev.slice(0, qty);
      }
      return prev; // No change needed
    });
  }, [receivedQuantity, isSerialized]); // FIXED: Removed serials.length from dependencies

  const handleSerialChange = (index: number, field: keyof SerialEntry, value: string) => {
    const newSerials = [...serials];
    newSerials[index] = { ...newSerials[index], [field]: value };
    setSerials(newSerials);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseInt(receivedQuantity, 10);
    if (isNaN(qty) || qty < 0) return;

    // Validate serials if serialized mode is enabled
    if (isSerialized) {
      const validSerials = serials.filter(s => s.serial_number.trim());
      if (validSerials.length !== qty) {
        return; // All serial numbers must be filled
      }
    }

    try {
      await onSubmit({
        received_quantity: qty,
        actual_location_id: locationId || undefined,
        lot_number: lotNumber || undefined,
        expiration_date: expirationDate || undefined,
        notes: notes || undefined,
        serials: isSerialized
          ? serials.map(s => ({
              serial_number: s.serial_number.trim(),
              mac_address: s.mac_address.trim() || undefined,
              dev_id: s.dev_id.trim() || undefined,
              device_status: s.device_status,
              notes: s.notes.trim() || undefined,
            }))
          : undefined,
      });
      toast.success('Line received successfully');
      handleClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to receive line: ${message}`);
      console.error('Error receiving line:', error);
      // Keep form open for retry
    }
  };

  const handleClose = () => {
    setReceivedQuantity('');
    setLocationId('');
    setLotNumber('');
    setExpirationDate('');
    setNotes('');
    setIsSerialized(false);
    setSerials([]);
    onClose();
  };

  // FIXED: Safe calculation with null checks
  const remaining = line
    ? (line.expected_quantity || 0) - (line.received_quantity || 0)
    : 0;

  const parsedQty = parseInt(receivedQuantity, 10);
  const validParsedQty = !isNaN(parsedQty) && parsedQty >= 0 ? parsedQty : 0;
  const allSerialsValid = !isSerialized || serials.every(s => s.serial_number.trim());

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className={`sm:max-w-[${isSerialized && validParsedQty > 0 ? '700px' : '500px'}] max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle>Receive Line</DialogTitle>
          <DialogDescription>
            Record the quantity received and storage location.
          </DialogDescription>
        </DialogHeader>
        {line && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Line info */}
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SKU:</span>
                  <span className="font-mono">{line.sku || line.sku_code || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Description:</span>
                  <span>{line.description || line.sku_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected:</span>
                  <span className="font-medium">{line.expected_quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Already Received:</span>
                  <span>{line.received_quantity}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-muted-foreground">Remaining:</span>
                  <span>{remaining}</span>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="received_quantity">Quantity Receiving *</Label>
                  <Input
                    id="received_quantity"
                    type="number"
                    min="0"
                    max={remaining}
                    value={receivedQuantity}
                    onChange={(e) => setReceivedQuantity(e.target.value)}
                    placeholder={`Max: ${remaining}`}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="storage_location">Storage Location *</Label>
                  <Select value={locationId} onValueChange={setLocationId}>
                    <SelectTrigger id="storage_location">
                      <SelectValue placeholder={locationsLoading ? "Loading..." : "Select location"} />
                    </SelectTrigger>
                    <SelectContent>
                      {locationsData?.locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.full_code || `${location.zone_code}-${location.bin_code}`}
                          {location.name && ` (${location.name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="lot_number">Lot Number</Label>
                    <Input
                      id="lot_number"
                      value={lotNumber}
                      onChange={(e) => setLotNumber(e.target.value)}
                      placeholder="Lot/Batch"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="expiration_date">Expiration Date</Label>
                    <Input
                      id="expiration_date"
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Serialized Items Toggle */}
                <div className="flex items-center justify-between py-2 border-t">
                  <div className="space-y-0.5">
                    <Label htmlFor="serialized">Track Serial Numbers</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable to record individual serial numbers for each unit
                    </p>
                  </div>
                  <Switch
                    id="serialized"
                    checked={isSerialized}
                    onCheckedChange={setIsSerialized}
                    disabled={!validParsedQty}
                  />
                </div>

                {/* Serial Number Entries */}
                {isSerialized && validParsedQty > 0 && (
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <Label>Serial Numbers ({serials.length} of {validParsedQty})</Label>
                      {!allSerialsValid && (
                        <span className="text-sm text-destructive">All serial numbers required</span>
                      )}
                    </div>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                      {serials.map((serial, index) => (
                        <div key={index} className="p-3 border rounded-md space-y-2 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Unit {index + 1}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Serial Number *</Label>
                              <Input
                                value={serial.serial_number}
                                onChange={(e) => handleSerialChange(index, 'serial_number', e.target.value)}
                                placeholder="Serial number"
                                className="mt-1 h-8 text-sm font-mono"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">MAC Address</Label>
                              <Input
                                value={serial.mac_address}
                                onChange={(e) => handleSerialChange(index, 'mac_address', e.target.value)}
                                placeholder="00:00:00:00:00:00"
                                className="mt-1 h-8 text-sm font-mono"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Device ID</Label>
                              <Input
                                value={serial.dev_id}
                                onChange={(e) => handleSerialChange(index, 'dev_id', e.target.value)}
                                placeholder="Device ID"
                                className="mt-1 h-8 text-sm font-mono"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Status</Label>
                              <Select
                                value={serial.device_status}
                                onValueChange={(v) => handleSerialChange(index, 'device_status', v)}
                              >
                                <SelectTrigger className="mt-1 h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {deviceStatusOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes about this receipt"
                    rows={2}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !receivedQuantity || !locationId || !allSerialsValid}>
                {isLoading ? 'Receiving...' : 'Receive'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
